'use client';
import React, { useState, useEffect } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { IconButton, Collapse } from '@mui/material';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import { useSelector } from 'react-redux';
import Link from 'next/link';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { UserAuth } from '@/app/context/AuthContext';
import { useRouter, usePathname } from 'next/navigation';
import InspirationMenu from '../@generalComponents/InspirationMenu';
import { GlobalLoader } from '../@generalComponents/GlobalLoader';
import { LoadingLink } from '../@generalComponents/LoadingLink';
import ProBadge from '../@generalComponents/ProBadge';

const Sidebar = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [categoriesExpanded, setCategoriesExpanded] = useState(false);
	const [inspirationExpanded, setInspirationExpanded] = useState(false);
	const [accountExpanded, setAccountExpanded] = useState(false);
	const categories = useSelector(
		(state) => state.categoryReducer.allCategories
	);
	const { user, isPro, googleSignIn, logOut } = UserAuth();
	const router = useRouter();
	const pathname = usePathname();

	useEffect(() => {
		const subscribeToMailchimp = async () => {
			if (user && user.email) {
				const mailchimpUrl =
					'https://tinytexts.us22.list-manage.com/subscribe/post-json?u=dc310015b31321bdba93b3b1a&id=b9c191733b&c=?';

				const formData = new FormData();
				formData.append('EMAIL', user.email);

				try {
					await fetch(mailchimpUrl, {
						method: 'POST',
						body: formData,
						mode: 'no-cors',
					});
				} catch (error) {
					console.error('Error subscribing to Mailchimp:', error);
				}
			}
		};

		subscribeToMailchimp();
	}, [user]);

	useEffect(() => {
		setIsLoading(false);
	}, [pathname]);

	const handleLogin = async () => {
		try {
			const result = await googleSignIn();
			if (!result?.user) return;

			if (result.user.email) {
				const mailchimpUrl =
					'https://tinytexts.us22.list-manage.com/subscribe/post-json?u=dc310015b31321bdba93b3b1a&id=b9c191733b&c=?';

				const formData = new FormData();
				formData.append('EMAIL', result.user.email);

				await fetch(mailchimpUrl, {
					method: 'POST',
					body: formData,
					mode: 'no-cors',
				});
			}

			setDrawerOpen(false);
			router.push('/explore');
		} catch (error) {
			console.error('Error signing in:', error);
		}
	};

	const handleLogout = async () => {
		try {
			await logOut();
			setDrawerOpen(false);
		} catch (error) {
			console.error('Error logging out:', error);
		}
	};

	const handleDrawerClose = () => {
		setDrawerOpen(false);
		setCategoriesExpanded(false);
		setInspirationExpanded(false);
		setAccountExpanded(false);
	};

	const handleCategoryClick = (loading) => {
		setIsLoading(loading);
		setDrawerOpen(false);
		setCategoriesExpanded(false);
	};

	const handleBrandClick = (loading) => {
		setIsLoading(loading);
		setDrawerOpen(false);
	};

	const hideLoader = () => {
		setIsLoading(false);
	};

	const menuItem = (label, onClick, extra = null) => (
		<button
			onClick={onClick}
			className='w-full text-center px-3 py-3 text-base font-medium text-gray-800 hover:bg-gray-50 transition-colors'
		>
			{label}{extra}
		</button>
	);

	return (
		<>
			<GlobalLoader
				isVisible={isLoading}
				onHide={hideLoader}
			/>
			<React.Fragment>
				<IconButton
					onClick={() => setDrawerOpen(true)}
					aria-label='Open menu'
					sx={{
						p: { xs: 1, sm: 1.5 },
						'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
					}}
				>
					<MenuIcon sx={{ color: 'black', fontSize: { xs: 24, sm: 28 } }} />
				</IconButton>

				<SwipeableDrawer
					anchor='right'
					PaperProps={{
						sx: {
							width: { xs: '85%', sm: '70%', md: '50%' },
							maxWidth: '400px',
							backgroundColor: '#ffffff',
						},
					}}
					open={drawerOpen}
					onClose={handleDrawerClose}
					onOpen={() => {}}
					disableBackdropTransition={false}
					disableDiscovery={false}
				>
					{/* Header */}
					<div className='flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50'>
						<div className='flex items-center'>
							<IconButton
								onClick={handleDrawerClose}
								aria-label='Close menu'
								sx={{
									mr: 1,
									p: 1,
									'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
								}}
							>
								<ArrowBackIosNewIcon sx={{ color: 'black', fontSize: 20 }} />
							</IconButton>
							<h2 className='text-xl font-semibold text-gray-800'>Menu</h2>
						</div>
					</div>

					<List sx={{ py: 0 }}>
						{/* Get PRO - visible to everyone (guests, free users, and Pro users) */}
						<ListItem disablePadding>
							{menuItem(
								user && isPro ? 'PRO ✓' : 'Get PRO',
								() => {
									if (!user) {
										router.push('/pro');
									} else if (isPro) {
										router.push('/account');
									} else {
										router.push('/pro');
									}
									setDrawerOpen(false);
								}
							)}
						</ListItem>
						<Divider />

						{/* Logged-in nav items above Categories */}
						{user && (
							<>
								<ListItem disablePadding>
									{menuItem(
										'Explore',
										() => { router.push('/explore'); setDrawerOpen(false); }
									)}
								</ListItem>
								<Divider />
								<ListItem disablePadding>
									<button
										onClick={() => {
											if (isPro) {
												router.push('/following');
											} else {
												router.push('/pro');
											}
											setDrawerOpen(false);
										}}
										className='w-full text-center px-3 py-3 text-base font-medium text-gray-800 hover:bg-gray-50 transition-colors flex items-center justify-center'
									>
										Following <ProBadge className='ml-2' />
									</button>
								</ListItem>
								<Divider />
								<ListItem disablePadding>
									{menuItem(
										'Bookmarks',
										() => { router.push('/bookmarks'); setDrawerOpen(false); }
									)}
								</ListItem>
								<Divider />
								<ListItem disablePadding>
									<button
										disabled
										className='w-full text-center px-3 py-3 text-base font-medium text-gray-400 flex items-center justify-center cursor-default'
									>
										Collections <ProBadge className='ml-2' />
										<span className='ml-1 text-xs text-gray-400'>(Coming Soon)</span>
									</button>
								</ListItem>
								<Divider />
							</>
						)}

						{/* Categories */}
						<ListItem disablePadding>
							<div className='w-full'>
								<IconButton
									onClick={() => setCategoriesExpanded(!categoriesExpanded)}
									sx={{
										display: 'flex',
										borderRadius: 0,
										width: '100%',
										position: 'relative',
										justifyContent: 'center',
										alignItems: 'center',
										px: 3,
										py: 2,
										'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
									}}
									aria-label='Toggle categories'
								>
									<span className='text-base font-medium text-gray-800'>
										Categories
									</span>
									<div style={{ position: 'absolute', right: '12px' }}>
										{categoriesExpanded ? (
											<ExpandLessIcon sx={{ color: 'gray', fontSize: 20 }} />
										) : (
											<ExpandMoreIcon sx={{ color: 'gray', fontSize: 20 }} />
										)}
									</div>
								</IconButton>

								<Collapse in={categoriesExpanded} timeout='auto' unmountOnExit>
									<div className='px-4 py-2 bg-white text-center'>
										{Array.isArray(categories) && categories.length > 0 ? (
											<div className='grid grid-cols-1'>
												{categories.map((cat, subIndex) => (
													<LoadingLink
														key={cat.categoryId || subIndex}
														href={`/categories/${cat?.slug || cat?.categoryId}`}
														className='block px-3 py-2 text-sm font-medium text-gray-800 transition-colors duration-200 rounded-md hover:bg-gray-50 hover:underline text-center'
														onLoadingChange={handleCategoryClick}
													>
														{cat?.name || 'Unnamed Category'}
													</LoadingLink>
												))}
											</div>
										) : (
											<p className='px-3 py-2 text-sm text-gray-500'>
												No categories available
											</p>
										)}
									</div>
								</Collapse>
							</div>
						</ListItem>

						<Divider />

						{/* Brands */}
						<ListItem disablePadding>
							<LoadingLink
								href='/brands'
								className='w-full'
								onLoadingChange={handleBrandClick}
							>
								<IconButton
									sx={{
										borderRadius: 0,
										display: 'flex',
										width: '100%',
										px: 3,
										py: 2,
										justifyContent: 'center',
										'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
									}}
									aria-label='Go to Brands page'
								>
									<span className='text-base font-medium text-gray-800'>
										Brands
									</span>
								</IconButton>
							</LoadingLink>
						</ListItem>

						<Divider />

						{/* Inspiration */}
						<ListItem disablePadding>
							<InspirationMenu
								isMobile={true}
								onItemClick={() => setDrawerOpen(false)}
								setIsLoading={setIsLoading}
							/>
						</ListItem>

						<Divider />

						{/* My Account / Sign In */}
						<ListItem disablePadding>
							<div className='w-full'>
								{user ? (
									<>
										<IconButton
											onClick={() => setAccountExpanded(!accountExpanded)}
											sx={{
												borderRadius: 0,
												width: '100%',
												justifyContent: 'center',
												px: 3,
												py: 2,
												'&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
												position: 'relative',
											}}
											aria-label='Toggle user menu'
										>
											<span className='text-base font-medium text-gray-800'>
												My Account
											</span>
											<div style={{ position: 'absolute', right: '12px' }}>
												{accountExpanded ? (
													<ExpandLessIcon sx={{ color: 'gray', fontSize: 20 }} />
												) : (
													<ExpandMoreIcon sx={{ color: 'gray', fontSize: 20 }} />
												)}
											</div>
										</IconButton>

										<Collapse in={accountExpanded} timeout='auto' unmountOnExit>
											<div className='flex flex-col bg-white'>
												{user?.email && (
													<div className='px-3 py-3 text-sm text-gray-500 text-center border-b border-gray-100 truncate'>
														{user.email}
													</div>
												)}
												<button
													onClick={() => { router.push('/account'); setDrawerOpen(false); }}
													className='w-full text-center px-3 py-3 text-base font-medium text-gray-800 hover:bg-gray-50 transition-colors'
												>
													Manage subscription
												</button>
												<button
													onClick={handleLogout}
													className='w-full text-center px-3 py-3 text-base font-medium text-gray-800 hover:bg-gray-50 transition-colors'
												>
													Sign out
												</button>
											</div>
										</Collapse>
									</>
								) : (
									<div className='flex flex-col bg-white'>
										<button
											onClick={handleLogin}
											className='w-full text-center px-3 py-3 text-base font-medium text-gray-800 hover:bg-gray-50 transition-colors'
										>
											Sign in
										</button>
										<Divider />
										<button
											onClick={handleLogin}
											className='w-full text-center px-3 py-3 text-base font-medium text-gray-800 hover:bg-gray-50 transition-colors'
										>
											Join for free
										</button>
									</div>
								)}
							</div>
						</ListItem>
					</List>
				</SwipeableDrawer>
			</React.Fragment>
		</>
	);
};

export default Sidebar;
